/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import React from 'react';
import { dateUtils, DateTimePicker } from '@opengeoweb/shared';
import { CalendarToday } from '@opengeoweb/theme';
import type { TimelineEditing } from './WarningEventTimelineBlock';
import {
  EXPANDED_BLOCK_TOP,
  getEndTimeForMovedStart,
  getTimeFieldPositions,
  MIN_DURATION_MINUTES,
  SNAP_MINUTES,
  TIME_FIELD_WIDTH,
} from './warningEventTimelineDragUtils';
import { useWarningsTranslation } from '../../../../utils/i18n';

type EditingField = 'start' | 'end' | null;

const EDIT_FIELD_SX = {
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(12px, 2px) scale(0.83)',
  },
  '& .MuiPickersSectionList-root': {
    fontSize: 12,
    paddingTop: '16px',
    paddingBottom: '4px',
  },
  '& .MuiInputAdornment-positionEnd': {
    paddingTop: '0 !important',
    '& .MuiTypography-root': { fontSize: 12 },
  },
  '& .MuiSvgIcon-root': { fontSize: 16 },
} as const;

interface TimeFieldProps {
  field: 'start' | 'end';
  label: string;
  time: Date;
  draft: Date | null;
  isDraftInvalid: boolean;
  left: number;
  isStackedAbove: boolean;
  isActive?: boolean;
  minPickerDate?: Date;
  maxPickerDate: Date;
  editingField: EditingField;
  onEditingFieldChange: (field: EditingField) => void;
  onDraftChange: (field: 'start' | 'end', value: Date) => void;
  onDraftValidityChange: (field: 'start' | 'end', isInvalid: boolean) => void;
  onCommitSession: (override?: { field: 'start' | 'end'; value: Date }) => void;
  onCancelSession: () => void;
}

const TimeField: React.FC<TimeFieldProps> = ({
  field,
  label,
  time,
  draft,
  isDraftInvalid,
  left,
  isStackedAbove,
  minPickerDate,
  maxPickerDate,
  editingField,
  onEditingFieldChange,
  onDraftChange,
  onDraftValidityChange,
  onCommitSession,
  onCancelSession,
  isActive = false,
}) => {
  const isEditing = editingField === field;
  const isPopupOpenRef = React.useRef(false);
  const shownTime = draft ?? time;

  return (
    <Box
      onKeyDown={(event): void => {
        if (event.key === 'Enter') {
          onCommitSession();
        }
        if (event.key === 'Escape') {
          onCancelSession();
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          const targetField = event.shiftKey ? 'start' : 'end';
          if (field !== targetField) {
            onEditingFieldChange(targetField);
          } else {
            onCommitSession();
          }
        }
      }}
      onPointerDown={(event): void => event.stopPropagation()}
      style={{ left }}
      sx={{
        position: 'absolute',
        top: EXPANDED_BLOCK_TOP,
        transform: isStackedAbove
          ? 'translateY(calc(-200% - 4px))'
          : 'translateY(-100%)',
        width: TIME_FIELD_WIDTH,
        zIndex: 4,
      }}
    >
      {isEditing && !isActive ? (
        <DateTimePicker
          autoFocus
          label={label}
          maxDateTime={maxPickerDate}
          minDateTime={minPickerDate}
          onAccept={(value): void => {
            if (isPopupOpenRef.current && value) {
              onCommitSession({ field, value });
            }
          }}
          onChange={(value): void => {
            if (value) {
              onDraftChange(field, value);
            }
          }}
          onClose={(): void => {
            isPopupOpenRef.current = false;
          }}
          onError={(reason): void => {
            onDraftValidityChange(field, reason != null);
          }}
          onOpen={(): void => {
            isPopupOpenRef.current = true;
          }}
          slotProps={{
            textField: {
              error: isDraftInvalid && draft != null,
            },
          }}
          sx={EDIT_FIELD_SX}
          timeSteps={{ minutes: SNAP_MINUTES }}
          value={shownTime}
        />
      ) : (
        <Box
          data-testid={`warning-event-block-${field}-field`}
          onDoubleClick={(): void => onEditingFieldChange(field)}
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            border: '1px solid',
            borderColor: 'geowebColors.cards.cardContainerBorder',
            padding: '2px 8px',
            cursor: !isActive ? 'pointer' : '',
            userSelect: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              color: 'geowebColors.typographyAndIcons.inactiveText',
            }}
          >
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 14 }} />
            <Typography
              sx={{
                fontSize: 12,
                color: isActive
                  ? 'geowebColors.typographyAndIcons.inactiveText'
                  : '',
              }}
            >
              {`${dateUtils.dateToString(shownTime, dateUtils.DATE_FORMAT_DATEPICKER, true)} UTC`}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

interface WarningEventTimelineBlockControlsProps {
  left: number;
  width: number;
  containerWidth: number;
  from: Date;
  until: Date;
  now: Date;
  maxPickerDate: Date;
  editing: TimelineEditing;
  showEditAffordances: boolean;
  isActive?: boolean;
}

export const WarningEventTimelineBlockControls: React.FC<
  WarningEventTimelineBlockControlsProps
> = ({
  left,
  width,
  containerWidth,
  from,
  until,
  now,
  maxPickerDate,
  editing,
  showEditAffordances,
  isActive = false,
}) => {
  const { t } = useWarningsTranslation();
  const [editingField, setEditingField] = React.useState<EditingField>(null);
  const [draftFrom, setDraftFrom] = React.useState<Date | null>(null);
  const [draftUntil, setDraftUntil] = React.useState<Date | null>(null);
  const [invalidFields, setInvalidFields] = React.useState({
    start: false,
    end: false,
  });
  // an end time that followed the start automatically may be shifted again
  const [isEndFollowingStart, setIsEndFollowingStart] = React.useState(false);

  const showFields = showEditAffordances || editingField !== null;

  if (!showFields) {
    return null;
  }

  const { startLeft, endLeft, isStacked } = getTimeFieldPositions({
    left,
    width,
    containerWidth,
  });

  // pickers mirror the drag bounds, including the minimum duration between the fields
  const minDurationMs = MIN_DURATION_MINUTES * 60_000;
  const effectiveFrom = draftFrom ?? from;
  const effectiveUntil = draftUntil ?? until;
  const startMaxPickerDate = new Date(
    Math.min(
      effectiveUntil.getTime() - minDurationMs,
      maxPickerDate.getTime() - minDurationMs,
    ),
  );
  const endMinPickerDate = new Date(
    Math.max(now.getTime(), effectiveFrom.getTime() + minDurationMs),
  );

  const endSession = (): void => {
    setDraftFrom(null);
    setDraftUntil(null);
    setInvalidFields({ start: false, end: false });
    setIsEndFollowingStart(false);
    setEditingField(null);
  };

  const onDraftChange = (field: 'start' | 'end', value: Date): void => {
    if (field === 'start') {
      setDraftFrom(value);
    } else {
      setDraftUntil(value);
      setIsEndFollowingStart(false);
    }
  };

  const shouldEndFollowStart = !draftUntil || isEndFollowingStart;

  const endFollowingStart = (startValue: Date): Date =>
    new Date(
      getEndTimeForMovedStart(
        from.getTime(),
        until.getTime(),
        startValue.getTime(),
        maxPickerDate.getTime(),
      ),
    );

  const onEditingFieldChangeWithShift = (nextField: EditingField): void => {
    if (editingField === 'start' && nextField !== 'start') {
      if (draftFrom && shouldEndFollowStart) {
        setDraftUntil(endFollowingStart(draftFrom));
        setIsEndFollowingStart(true);
      }
    }
    setEditingField(nextField);
  };

  const onDraftValidityChange = (
    field: 'start' | 'end',
    isInvalid: boolean,
  ): void => {
    setInvalidFields((previous) =>
      previous[field] === isInvalid
        ? previous
        : { ...previous, [field]: isInvalid },
    );
  };

  const onCommitSession = (override?: {
    field: 'start' | 'end';
    value: Date;
  }): void => {
    const hasInvalidDraft =
      (draftFrom && invalidFields.start) || (draftUntil && invalidFields.end);
    if (hasInvalidDraft) {
      return;
    }
    const finalFrom = override?.field === 'start' ? override.value : draftFrom;
    const typedUntil = shouldEndFollowStart ? null : draftUntil;
    const explicitUntil =
      override?.field === 'end' ? override.value : typedUntil;
    const finalUntil =
      finalFrom && !explicitUntil
        ? endFollowingStart(finalFrom)
        : explicitUntil;
    if (finalFrom || finalUntil) {
      editing.onTimesChange(
        (finalFrom ?? from).toISOString(),
        (finalUntil ?? until).toISOString(),
      );
    }
    endSession();
  };

  return (
    <>
      <TimeField
        draft={draftFrom}
        editingField={editingField}
        field="start"
        isActive={isActive}
        isDraftInvalid={invalidFields.start}
        isStackedAbove={isStacked}
        label={t('warning-list-header-start-time')}
        left={startLeft}
        maxPickerDate={startMaxPickerDate}
        onCancelSession={endSession}
        onCommitSession={onCommitSession}
        onDraftChange={onDraftChange}
        onDraftValidityChange={onDraftValidityChange}
        onEditingFieldChange={onEditingFieldChangeWithShift}
        time={from}
      />
      <TimeField
        draft={draftUntil}
        editingField={editingField}
        field="end"
        isDraftInvalid={invalidFields.end}
        isStackedAbove={false}
        label={t('warning-list-header-end-time')}
        left={endLeft}
        maxPickerDate={maxPickerDate}
        minPickerDate={endMinPickerDate}
        onCancelSession={endSession}
        onCommitSession={onCommitSession}
        onDraftChange={onDraftChange}
        onDraftValidityChange={onDraftValidityChange}
        onEditingFieldChange={onEditingFieldChangeWithShift}
        time={until}
      />
    </>
  );
};
